import { useChatbot } from '@/feature/chatbot/useChatbot';
import { useChatbotHistory } from '@/feature/chatbot/useChatbotHistory';
import { useTexts } from '@/feature/lang';
import '@/icon/MaterialSymbol/font';
import { formatDateAndTime } from '@/util/date';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Translate from '../Translate';
import { HISTORY_WIDTH } from './styles';

const HistoryContainer = styled('div')(({ theme }) => ({
  width: HISTORY_WIDTH,
  flexShrink: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const HistoryHeader = styled('div')(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: theme.palette.text.secondary,
  width: '100%',
}));

const HistoryList = styled(List)(({ theme }) => ({
  overflow: 'auto',
  flexGrow: 1,
}));

const HistoryListItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  marginBottom: theme.spacing(0.5),
  backgroundColor: isActive ? theme.palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: isActive
      ? theme.palette.action.selected
      : theme.palette.action.hover,
  },
  position: 'relative',
}));

const ActionButtons = styled('div')(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  gap: theme.spacing(0.5),
  opacity: 0,
  width: 0,
  overflow: 'hidden',
  transition: 'opacity 0.2s ease, width 0.2s ease',
  '.MuiListItemButton-root:hover &': {
    opacity: 1,
    width: 'auto',
  },
}));

const EmptyStateContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  height: '100%',
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

interface HistoryProps {
  onClose?: () => void;
}

export const History = ({ onClose }: HistoryProps) => {
  const texts = useTexts().component.chatbot.history;
  const { conversationId, createNew, assignConversation } = useChatbot();
  const { history } = useChatbotHistory();

  return (
    <HistoryContainer>
      <HistoryHeader>
        <Tooltip title="New conversation">
          <IconButton size="small" onClick={createNew}>
            <span className="material-symbols-rounded">add</span>
          </IconButton>
        </Tooltip>
        {onClose && (
          <Tooltip title="Close history">
            <IconButton size="small" onClick={onClose}>
              <span className="material-symbols-rounded">close</span>
            </IconButton>
          </Tooltip>
        )}
      </HistoryHeader>

      <HistoryList>
        {history.length === 0 ? (
          <EmptyStateContainer>
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 48, opacity: 0.5, marginBottom: 8 }}
            >
              history
            </span>
            <Typography variant="body2">
              <Translate text={texts.noHistory.text} />
            </Typography>
          </EmptyStateContainer>
        ) : (
          history
            .slice()
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            )
            .map((item) => (
              <ListItem key={item.id} disablePadding>
                <HistoryListItem
                  onClick={() => assignConversation(item)}
                  isActive={item.id === conversationId}
                >
                  <Box component="div" sx={{ width: '100%' }}>
                    <Typography variant="subtitle2" noWrap>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {formatDateAndTime(item.createdAt)}
                    </Typography>
                  </Box>

                  <ActionButtons>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        aria-label="delete conversation"
                        color="error"
                      >
                        <span
                          className="material-symbols-rounded"
                          style={{ fontSize: 18 }}
                        >
                          delete
                        </span>
                      </IconButton>
                    </Tooltip>
                  </ActionButtons>
                </HistoryListItem>
              </ListItem>
            ))
        )}
      </HistoryList>
    </HistoryContainer>
  );
};
